import { useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  archiveNews,
  deleteNews,
  getApiErrorMessage,
  getNews,
  listAllNewsComments,
  listAllNewsMedia,
  listNews,
  listNewsCategories,
  listNewsTags,
  publishNews,
  type NewsItem,
  type NewsStatus,
  unpublishNews,
} from '../lib/api';
import { extractTextFromContentBlocks, isContentBlockMediaTitle, renderContentBlocksHtml } from '../lib/contentBlocks';
import { NEWS_LIST_TEMPLATE } from '../templates/newsListTemplate';

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const formatDateTime = (value?: string | null): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const statusLabel = (status: NewsStatus): string => {
  if (status === 'pending_review') return 'Pending Review';
  if (status === 'published') return 'Published';
  if (status === 'archived') return 'Archived';
  return 'Draft';
};

const statusBadgeClass = (status: NewsStatus): string => {
  if (status === 'published') return 'bg-emerald-50 text-emerald-600 border-emerald-100';
  if (status === 'pending_review') return 'bg-yellow-50 text-yellow-600 border-yellow-100';
  if (status === 'archived') return 'bg-slate-100 text-slate-600 border-slate-200';
  return 'bg-blue-50 text-blue-600 border-blue-100';
};

const drawerStatusClass = (status: NewsStatus): string => {
  if (status === 'published') return 'bg-emerald-100 text-emerald-700';
  if (status === 'pending_review') return 'bg-yellow-100 text-yellow-700';
  if (status === 'archived') return 'bg-slate-100 text-slate-600';
  return 'bg-blue-100 text-blue-700';
};

export default function NewsPage() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const template = useMemo(() => NEWS_LIST_TEMPLATE, []);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const searchInput = root.querySelector<HTMLInputElement>('#news-search');
    const statusFilter = root.querySelector<HTMLSelectElement>('#news-filter-status');
    const categoryFilter = root.querySelector<HTMLSelectElement>('#news-filter-category');
    const tagFilter = root.querySelector<HTMLSelectElement>('#news-filter-tag');
    const featuredFilter = root.querySelector<HTMLButtonElement>('#news-filter-featured');
    const pinnedFilter = root.querySelector<HTMLButtonElement>('#news-filter-pinned');
    const ordering = root.querySelector<HTMLSelectElement>('#news-ordering');
    const tbody = root.querySelector<HTMLTableSectionElement>('#news-table-body');
    const meta = root.querySelector<HTMLElement>('#news-meta');
    const prevBtn = root.querySelector<HTMLButtonElement>('#news-prev-btn');
    const nextBtn = root.querySelector<HTMLButtonElement>('#news-next-btn');
    const pageIndicator = root.querySelector<HTMLElement>('#news-page-indicator');
    const resetFiltersBtn = root.querySelector<HTMLButtonElement>('#news-reset-filters-btn');
    const kpiPublished = root.querySelector<HTMLElement>('#news-kpi-published');
    const kpiPending = root.querySelector<HTMLElement>('#news-kpi-pending');
    const kpiDraft = root.querySelector<HTMLElement>('#news-kpi-draft');
    const kpiArchived = root.querySelector<HTMLElement>('#news-kpi-archived');

    const overlay = root.querySelector<HTMLElement>('#news-view-overlay');
    const backdrop = root.querySelector<HTMLElement>('#news-view-backdrop');
    const closeBtn = root.querySelector<HTMLButtonElement>('#news-view-close-btn');
    const editLink = root.querySelector<HTMLAnchorElement>('#news-view-edit');
    const viewTitle = root.querySelector<HTMLElement>('#news-view-title');
    const viewId = root.querySelector<HTMLElement>('#news-view-id');
    const viewStatus = root.querySelector<HTMLElement>('#news-view-status');
    const viewViews = root.querySelector<HTMLElement>('#news-view-views');
    const viewRating = root.querySelector<HTMLElement>('#news-view-rating');
    const viewComments = root.querySelector<HTMLElement>('#news-view-comments');
    const viewCommentsCount = root.querySelector<HTMLElement>('#news-view-comments-count');
    const viewCommentsList = root.querySelector<HTMLElement>('#news-view-comments-list');
    const viewBookmarks = root.querySelector<HTMLElement>('#news-view-bookmarks');
    const viewReadTime = root.querySelector<HTMLElement>('#news-view-read-time');
    const viewCover = root.querySelector<HTMLImageElement>('#news-view-cover');
    const viewGallery = root.querySelector<HTMLElement>('#news-view-gallery');
    const viewSlug = root.querySelector<HTMLElement>('#news-view-slug');
    const viewExcerpt = root.querySelector<HTMLElement>('#news-view-excerpt');
    const viewDescription = root.querySelector<HTMLElement>('#news-view-description');
    const viewContent = root.querySelector<HTMLElement>('#news-view-content');
    const viewCategory = root.querySelector<HTMLElement>('#news-view-category');
    const viewTags = root.querySelector<HTMLElement>('#news-view-tags');
    const viewSourceName = root.querySelector<HTMLElement>('#news-view-source-name');
    const viewSourceUrl = root.querySelector<HTMLAnchorElement>('#news-view-source-url');
    const viewExternalUrl = root.querySelector<HTMLAnchorElement>('#news-view-external-url');
    const viewPublishedAt = root.querySelector<HTMLElement>('#news-view-published-at');
    const viewUpdatedAt = root.querySelector<HTMLElement>('#news-view-updated-at');
    const viewSeoTitle = root.querySelector<HTMLElement>('#news-view-seo-title');
    const viewSeoDescription = root.querySelector<HTMLElement>('#news-view-seo-description');
    const viewReviewComment = root.querySelector<HTMLElement>('#news-view-review-comment');
    const viewPublish = root.querySelector<HTMLButtonElement>('#news-view-publish');
    const viewArchive = root.querySelector<HTMLButtonElement>('#news-view-archive');
    const viewUnpublish = root.querySelector<HTMLButtonElement>('#news-view-unpublish');
    const viewDelete = root.querySelector<HTMLButtonElement>('#news-view-delete');

    if (
      !searchInput ||
      !statusFilter ||
      !categoryFilter ||
      !tagFilter ||
      !featuredFilter ||
      !pinnedFilter ||
      !ordering ||
      !tbody ||
      !meta ||
      !prevBtn ||
      !nextBtn ||
      !pageIndicator ||
      !resetFiltersBtn ||
      !kpiPublished ||
      !kpiPending ||
      !kpiDraft ||
      !kpiArchived ||
      !overlay ||
      !backdrop ||
      !closeBtn ||
      !editLink ||
      !viewTitle ||
      !viewId ||
      !viewStatus ||
      !viewViews ||
      !viewRating ||
      !viewComments ||
      !viewCommentsCount ||
      !viewCommentsList ||
      !viewBookmarks ||
      !viewReadTime ||
      !viewCover ||
      !viewGallery ||
      !viewSlug ||
      !viewExcerpt ||
      !viewDescription ||
      !viewContent ||
      !viewCategory ||
      !viewTags ||
      !viewSourceName ||
      !viewSourceUrl ||
      !viewExternalUrl ||
      !viewPublishedAt ||
      !viewUpdatedAt ||
      !viewSeoTitle ||
      !viewSeoDescription ||
      !viewReviewComment ||
      !viewPublish ||
      !viewArchive ||
      !viewUnpublish ||
      !viewDelete
    ) {
      return;
    }

    let disposed = false;
    let searchTimer: number | null = null;
    let currentPage = 1;
    const pageSize = 20;
    let newsItems: NewsItem[] = [];
    let openedId: number | null = null;
    let viewRequestId = 0;
    let featuredOnly = false;
    let pinnedOnly = false;

    const categoriesById = new Map<number, string>();
    const tagsById = new Map<number, string>();

    const setViewParam = (value: string | null) => {
      const params = new URLSearchParams(window.location.search);
      if (value) params.set('view', value);
      else params.delete('view');
      const query = params.toString();
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
    };

    const renderRows = (items: NewsItem[]) => {
      if (!items.length) {
        tbody.innerHTML = '<tr><td class="px-6 py-8 text-sm text-slate-500" colspan="10">No news found.</td></tr>';
        return;
      }

      tbody.innerHTML = items
        .map((item) => {
          const tags = item.tag_ids
            .map((id) => `<span class="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded text-slate-600 border border-slate-200">${escapeHtml(tagsById.get(id) || `#${id}`)}</span>`)
            .join('');

          return `
            <tr class="hover:bg-slate-50/80 transition-colors ${openedId === item.id ? 'bg-primary/5' : ''}">
              <td class="px-6 py-4 text-xs font-mono text-slate-400">#ART-${item.id}</td>
              <td class="px-6 py-4">
                <div class="flex flex-col">
                  <button data-news-action="view" data-news-id="${item.id}" class="text-left text-sm font-bold text-slate-900 hover:text-primary">${escapeHtml(item.title)}</button>
                  <span class="text-xs text-slate-400 font-mono">/news/${escapeHtml(item.slug)}</span>
                </div>
              </td>
              <td class="px-6 py-4"><span class="text-xs font-semibold px-2 py-1 bg-blue-50 text-blue-600 rounded">${escapeHtml(categoriesById.get(item.category) || '-')}</span></td>
              <td class="px-6 py-4"><div class="flex flex-wrap gap-1">${tags || '<span class="text-[10px] text-slate-400">-</span>'}</div></td>
              <td class="px-6 py-4 text-center"><span class="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-tight px-2 py-0.5 rounded-full border ${statusBadgeClass(item.status)}"><span class="w-1.5 h-1.5 rounded-full bg-current"></span>${statusLabel(item.status)}</span></td>
              <td class="px-6 py-4 text-center">${item.is_featured ? '<span class="material-symbols-outlined text-yellow-500" style="font-variation-settings:\'FILL\' 1">star</span>' : '<span class="material-symbols-outlined text-slate-300">star</span>'}</td>
              <td class="px-6 py-4 text-center">${item.is_pinned ? '<span class="material-symbols-outlined text-primary" style="font-variation-settings:\'FILL\' 1">push_pin</span>' : '<span class="material-symbols-outlined text-slate-300">push_pin</span>'}</td>
              <td class="px-6 py-4">
                <div class="flex items-center gap-4 text-xs">
                  <div class="flex flex-col items-center"><span class="font-bold">${item.view_count}</span><span class="text-[10px] text-slate-400">Views</span></div>
                  <div class="flex flex-col items-center"><span class="font-bold">${item.rating_avg}</span><span class="text-[10px] text-slate-400">${item.rating_count} rtg</span></div>
                  <div class="flex flex-col items-center"><span class="font-bold">${item.comments_count}</span><span class="text-[10px] text-slate-400">Comm</span></div>
                </div>
              </td>
              <td class="px-6 py-4"><div class="flex flex-col text-[11px]"><span class="text-slate-600 font-medium">Pub: ${escapeHtml(formatDateTime(item.published_at))}</span><span class="text-slate-400">Upd: ${escapeHtml(formatDateTime(item.updated_at))}</span></div></td>
              <td class="px-6 py-4 text-right">
                <div class="flex items-center justify-end gap-1 text-slate-400">
                  <button data-news-action="view" data-news-id="${item.id}" class="p-1 hover:text-primary" type="button"><span class="material-symbols-outlined text-[20px] pointer-events-none">visibility</span></button>
                  <a href="/news/create?id=${item.id}" class="inline-flex p-1 hover:text-primary"><span class="material-symbols-outlined text-[20px] pointer-events-none">edit</span></a>
                  <button data-news-action="delete" data-news-id="${item.id}" class="p-1 hover:text-rose-500" type="button"><span class="material-symbols-outlined text-[20px] pointer-events-none">delete</span></button>
                </div>
              </td>
            </tr>
          `;
        })
        .join('');
    };

    const loadCounters = async () => {
      try {
        const [published, pending, draft, archived] = await Promise.all([
          listNews({ status: 'published', page: 1, page_size: 1 }),
          listNews({ status: 'pending_review', page: 1, page_size: 1 }),
          listNews({ status: 'draft', page: 1, page_size: 1 }),
          listNews({ status: 'archived', page: 1, page_size: 1 }),
        ]);
        if (disposed) return;
        kpiPublished.textContent = String(published.count);
        kpiPending.textContent = String(pending.count);
        kpiDraft.textContent = String(draft.count);
        kpiArchived.textContent = String(archived.count);
      } catch {
        if (disposed) return;
        kpiPublished.textContent = '-';
        kpiPending.textContent = '-';
        kpiDraft.textContent = '-';
        kpiArchived.textContent = '-';
      }
    };

    const loadNews = async (page = currentPage) => {
      currentPage = Math.max(1, page);
      try {
        tbody.innerHTML = '<tr><td class="px-6 py-8 text-sm text-slate-500" colspan="10">Loading news...</td></tr>';

        const params: Record<string, string | number | boolean> = {
          ordering: ordering.value,
          page: currentPage,
          page_size: pageSize,
        };

        const query = searchInput.value.trim();
        if (query) params.search = query;
        if (statusFilter.value) params.status = statusFilter.value;
        if (categoryFilter.value) params.category = categoryFilter.value;
        if (tagFilter.value) params.tags = tagFilter.value;
        if (featuredOnly) params.is_featured = true;
        if (pinnedOnly) params.is_pinned = true;

        const data = await listNews(params);
        if (disposed) return;

        newsItems = data.results;

        renderRows(newsItems);
        const from = data.count === 0 ? 0 : (currentPage - 1) * pageSize + 1;
        const to = data.count === 0 ? 0 : Math.min(from + newsItems.length - 1, data.count);
        meta.textContent = `Showing ${from}-${to} of ${data.count} results`;
        pageIndicator.textContent = `Page ${currentPage}`;
        prevBtn.disabled = !data.previous;
        nextBtn.disabled = !data.next;
      } catch (error) {
        if (disposed) return;
        tbody.innerHTML = `<tr><td class="px-6 py-8 text-sm text-rose-600" colspan="10">${escapeHtml(getApiErrorMessage(error, 'Failed to load news.'))}</td></tr>`;
        pageIndicator.textContent = `Page ${currentPage}`;
        prevBtn.disabled = true;
        nextBtn.disabled = true;
      }
    };

    const fillView = async (id: number) => {
      openedId = id;
      const requestId = ++viewRequestId;
      setViewParam(String(id));
      overlay.classList.remove('hidden');
      viewTitle.textContent = 'Loading article...';
      viewId.textContent = `#ART-${id}`;
      viewStatus.textContent = 'Loading';
      viewViews.textContent = '-';
      viewRating.textContent = '-';
      viewComments.textContent = '-';
      viewCommentsCount.textContent = '0';
      viewCommentsList.innerHTML = '<p class="text-sm text-slate-500">Loading comments...</p>';
      viewBookmarks.textContent = '-';
      viewReadTime.textContent = '-';
      viewCover.src =
        "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='1200' height='675'><rect width='100%' height='100%' fill='%23e2e8f0'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='%2364758b' font-size='30' font-family='Arial'>Loading</text></svg>";
      viewGallery.innerHTML = '';
      viewSlug.textContent = '-';
      viewExcerpt.textContent = '-';
      viewDescription.textContent = '-';
      viewContent.textContent = 'Loading...';
      viewCategory.textContent = '-';
      viewTags.innerHTML = '<span class="text-sm text-slate-400">-</span>';
      viewSourceName.textContent = '-';
      viewSourceUrl.textContent = '-';
      viewSourceUrl.href = '#';
      viewExternalUrl.textContent = '-';
      viewExternalUrl.href = '#';
      viewPublishedAt.textContent = '-';
      viewUpdatedAt.textContent = '-';
      viewSeoTitle.textContent = '-';
      viewSeoDescription.textContent = '-';
      viewReviewComment.textContent = '-';
      editLink.href = `/news/create?id=${id}`;

      try {
        const [item, mediaPayload, commentsPayload] = await Promise.all([getNews(id), listAllNewsMedia(id), listAllNewsComments(id)]);
        if (disposed || requestId !== viewRequestId || openedId !== id) return;

        viewTitle.textContent = item.title;
        viewId.textContent = `#ART-${item.id}`;
        viewStatus.textContent = statusLabel(item.status);
        viewStatus.className = `px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider ${drawerStatusClass(item.status)}`;
        viewViews.textContent = String(item.view_count);
        viewRating.textContent = item.rating_avg;
        viewComments.textContent = String(item.comments_count);
        viewCommentsCount.textContent = String(commentsPayload.count);
        viewCommentsList.innerHTML = commentsPayload.results.length
          ? commentsPayload.results
              .slice(0, 20)
              .map(
                (comment) => `
                  <article class="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <div class="mb-1 flex items-center justify-between gap-3">
                      <div class="flex items-center gap-2">
                        ${
                          comment.author?.avatar
                            ? `<img src="${escapeHtml(comment.author.avatar)}" alt="${escapeHtml(comment.author?.name || comment.author_name || `User #${comment.user}`)}" class="h-6 w-6 rounded-full object-cover" />`
                            : `<span class="inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-200 text-[10px] font-bold text-slate-700">${escapeHtml((comment.author?.name || comment.author_name || `User #${comment.user}`).trim().charAt(0).toUpperCase() || 'U')}</span>`
                        }
                        <p class="text-xs font-semibold text-slate-700">${escapeHtml(comment.author?.name || comment.author_name || `User #${comment.user}`)}</p>
                      </div>
                      <span class="inline-flex rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-700">${escapeHtml(comment.status)}</span>
                    </div>
                    <p class="text-sm text-slate-700">${escapeHtml(comment.message || '')}</p>
                    <p class="mt-1 text-[11px] text-slate-500">${escapeHtml(formatDateTime(comment.created_at))}</p>
                  </article>
                `
              )
              .join('')
          : '<p class="text-sm text-slate-500">No comments yet.</p>';
        viewBookmarks.textContent = String(item.bookmarks_count);
        viewReadTime.textContent = `${item.reading_time_minutes}m`;
        const sortedMedia = mediaPayload.results
          .filter((entry) => !isContentBlockMediaTitle(entry.title))
          .sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);
        const cover =
          item.cover_image ||
          sortedMedia[0]?.file_url ||
          "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='1200' height='675'><rect width='100%' height='100%' fill='%23e2e8f0'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='%2364758b' font-size='30' font-family='Arial'>No cover</text></svg>";
        viewCover.src = cover;
        viewGallery.innerHTML = sortedMedia.length
          ? sortedMedia
              .slice(0, 8)
              .map(
                (entry) =>
                  `<div class="aspect-square rounded-lg border border-slate-200 overflow-hidden bg-slate-100"><img class="w-full h-full object-cover" src="${escapeHtml(
                    entry.file_url
                  )}" alt="${escapeHtml(entry.title || `media-${entry.id}`)}"/></div>`
              )
              .join('')
          : '<div class="col-span-4 text-xs text-slate-500">No gallery media.</div>';
        viewSlug.textContent = item.slug;
        viewExcerpt.textContent = item.excerpt || '-';
        viewDescription.textContent = item.description || '-';
        const renderedBlocks = renderContentBlocksHtml(item.content_blocks);
        if (renderedBlocks) {
          viewContent.innerHTML = renderedBlocks;
        } else {
          viewContent.textContent = item.content || extractTextFromContentBlocks(item.content_blocks) || '-';
        }
        viewCategory.textContent = item.category_detail?.name || categoriesById.get(item.category) || '-';
        viewTags.innerHTML = (item.tags?.length || item.tag_ids.length)
          ? (item.tags?.length
              ? item.tags
                  .map((tag) => `<span class="text-xs bg-primary/10 text-primary px-2 py-1 rounded">${escapeHtml(tag.name)}</span>`)
                  .join('')
              : item.tag_ids
                  .map((tagId) => `<span class="text-xs bg-primary/10 text-primary px-2 py-1 rounded">${escapeHtml(tagsById.get(tagId) || `#${tagId}`)}</span>`)
                  .join(''))
          : '<span class="text-sm text-slate-400">-</span>';
        viewSourceName.textContent = item.source_name || '-';

        if (item.source_url) {
          viewSourceUrl.textContent = item.source_url;
          viewSourceUrl.href = item.source_url;
        } else {
          viewSourceUrl.textContent = '-';
          viewSourceUrl.href = '#';
        }
        if (item.external_url) {
          viewExternalUrl.textContent = item.external_url;
          viewExternalUrl.href = item.external_url;
        } else {
          viewExternalUrl.textContent = '-';
          viewExternalUrl.href = '#';
        }

        viewPublishedAt.textContent = formatDateTime(item.published_at);
        viewUpdatedAt.textContent = formatDateTime(item.updated_at);
        viewSeoTitle.textContent = item.seo_title || '-';
        viewSeoDescription.textContent = item.seo_description || '-';
        viewReviewComment.textContent = item.review_comment || '-';
        editLink.href = `/news/create?id=${item.id}`;
      } catch (error) {
        if (disposed || requestId !== viewRequestId || openedId !== id) return;
        overlay.classList.remove('hidden');
        viewTitle.textContent = 'Failed to load article';
        viewContent.textContent = getApiErrorMessage(error, 'Failed to load article details.');
      }
    };

    const closeView = () => {
      openedId = null;
      viewRequestId += 1;
      overlay.classList.add('hidden');
      setViewParam(null);
      renderRows(newsItems);
    };

    const onTableClick = (event: Event) => {
      const target = event.target as HTMLElement;
      const button = target.closest<HTMLButtonElement>('button[data-news-action]');
      if (!button) return;

      const action = button.dataset.newsAction;
      const id = Number(button.dataset.newsId);
      if (!Number.isFinite(id) || id <= 0) return;

      if (action === 'view') {
        void fillView(id);
        return;
      }
      if (action === 'delete') {
        if (window.confirm('Delete this news article?')) {
          void (async () => {
            await deleteNews(id);
            await Promise.all([loadNews(), loadCounters()]);
          })();
        }
      }
    };

    const handleSearchInput = () => {
      if (searchTimer) window.clearTimeout(searchTimer);
      searchTimer = window.setTimeout(() => {
        currentPage = 1;
        void loadNews(1);
      }, 300);
    };

    const toggleButtonState = (button: HTMLButtonElement, active: boolean) => {
      if (active) {
        button.classList.add('bg-primary/10', 'border-primary/30', 'text-primary');
      } else {
        button.classList.remove('bg-primary/10', 'border-primary/30', 'text-primary');
      }
    };

    const initFilters = async () => {
      const [categories, tags] = await Promise.all([listNewsCategories(), listNewsTags()]);

      categories.forEach((item) => {
        categoriesById.set(item.id, item.name);
        categoryFilter.insertAdjacentHTML('beforeend', `<option value="${item.id}">${escapeHtml(item.name)}</option>`);
      });

      tags.forEach((item) => {
        tagsById.set(item.id, item.name);
        tagFilter.insertAdjacentHTML('beforeend', `<option value="${item.id}">${escapeHtml(item.name)}</option>`);
      });
    };

    const params = new URLSearchParams(window.location.search);
    const viewIdFromUrl = Number(params.get('view'));

    const handleOverlayAction = async (action: 'publish' | 'archive' | 'unpublish' | 'delete') => {
      if (!openedId) return;
      if (action === 'delete') {
        if (!window.confirm('Delete this news article?')) return;
        await deleteNews(openedId);
        closeView();
        await Promise.all([loadNews(), loadCounters()]);
        return;
      }
      if (action === 'publish') await publishNews(openedId);
      if (action === 'archive') await archiveNews(openedId);
      if (action === 'unpublish') await unpublishNews(openedId);
      await fillView(openedId);
      await Promise.all([loadNews(), loadCounters()]);
    };

    const onStatusChange = () => {
      currentPage = 1;
      void loadNews(1);
    };
    const onCategoryChange = () => {
      currentPage = 1;
      void loadNews(1);
    };
    const onTagChange = () => {
      currentPage = 1;
      void loadNews(1);
    };
    const onOrderingChange = () => {
      currentPage = 1;
      void loadNews(1);
    };
    const onResetFilters = () => {
      searchInput.value = '';
      statusFilter.value = '';
      categoryFilter.value = '';
      tagFilter.value = '';
      ordering.value = '-published_at';
      featuredOnly = false;
      pinnedOnly = false;
      toggleButtonState(featuredFilter, false);
      toggleButtonState(pinnedFilter, false);
      currentPage = 1;
      void loadNews(1);
    };
    const onPrevPage = () => {
      if (currentPage <= 1) return;
      void loadNews(currentPage - 1);
    };
    const onNextPage = () => {
      void loadNews(currentPage + 1);
    };

    searchInput.addEventListener('input', handleSearchInput);
    statusFilter.addEventListener('change', onStatusChange);
    categoryFilter.addEventListener('change', onCategoryChange);
    tagFilter.addEventListener('change', onTagChange);
    ordering.addEventListener('change', onOrderingChange);
    resetFiltersBtn.addEventListener('click', onResetFilters);
    featuredFilter.addEventListener('click', () => {
      featuredOnly = !featuredOnly;
      toggleButtonState(featuredFilter, featuredOnly);
      currentPage = 1;
      void loadNews(1);
    });
    pinnedFilter.addEventListener('click', () => {
      pinnedOnly = !pinnedOnly;
      toggleButtonState(pinnedFilter, pinnedOnly);
      currentPage = 1;
      void loadNews(1);
    });

    tbody.addEventListener('click', onTableClick);

    closeBtn.addEventListener('click', closeView);
    backdrop.addEventListener('click', closeView);
    viewPublish.addEventListener('click', () => void handleOverlayAction('publish'));
    viewArchive.addEventListener('click', () => void handleOverlayAction('archive'));
    viewUnpublish.addEventListener('click', () => void handleOverlayAction('unpublish'));
    viewDelete.addEventListener('click', () => void handleOverlayAction('delete'));
    prevBtn.addEventListener('click', onPrevPage);
    nextBtn.addEventListener('click', onNextPage);

    void (async () => {
      try {
        await initFilters();
        await Promise.all([loadNews(), loadCounters()]);
        if (Number.isFinite(viewIdFromUrl) && viewIdFromUrl > 0) {
          await fillView(viewIdFromUrl);
        }
      } catch (error) {
        tbody.innerHTML = `<tr><td class="px-6 py-8 text-sm text-rose-600" colspan="10">${escapeHtml(getApiErrorMessage(error, 'Failed to load news.'))}</td></tr>`;
      }
    })();

    return () => {
      disposed = true;
      if (searchTimer) window.clearTimeout(searchTimer);
      searchInput.removeEventListener('input', handleSearchInput);
      statusFilter.removeEventListener('change', onStatusChange);
      categoryFilter.removeEventListener('change', onCategoryChange);
      tagFilter.removeEventListener('change', onTagChange);
      ordering.removeEventListener('change', onOrderingChange);
      resetFiltersBtn.removeEventListener('click', onResetFilters);
      tbody.removeEventListener('click', onTableClick);
      prevBtn.removeEventListener('click', onPrevPage);
      nextBtn.removeEventListener('click', onNextPage);
      closeBtn.removeEventListener('click', closeView);
      backdrop.removeEventListener('click', closeView);
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>MotoJourney | News</title>
      </Helmet>
      <div className="font-display bg-background-light text-slate-900 min-h-screen">
        <div ref={containerRef} dangerouslySetInnerHTML={{ __html: template }} />
      </div>
    </>
  );
}
